(() => {
  if (customElements.get('smn-delivery-choice')) return;
  let pending = false;
  const choices = () => document.querySelectorAll('smn-delivery-choice');
  const setBusy = (busy) => {
    pending = busy;
    choices().forEach((choice) => {
      choice.setAttribute('aria-busy', String(busy));
      choice.querySelectorAll('[data-smn-delivery-mode], [data-smn-clear-test]').forEach((button) => {
        button.disabled = busy || choice.scopeActive === false || (button.dataset.smnDeliveryMode === 'pickup' && choice.dataset.rulesRequired === 'true' && choice.rulesBlocked !== false);
      });
    });
  };
  const setFields = (choice, mode = '', marker = '') => {
    const modeField = choice.querySelector('[data-smn-delivery-attribute]');
    const testField = choice.querySelector('[data-smn-test-attribute]');
    if (modeField) modeField.value = mode;
    if (testField) testField.value = marker;
  };
  const showStatus = (message, error = false) => {
    choices().forEach((choice) => {
      choice.toggleAttribute('data-has-error', error);
      const status = choice.querySelector('.smn-delivery-choice__status');
      if (status) status.textContent = message;
    });
  };
  const saved = (cart, mode, marker) => {
    const attributes = cart?.attributes || {};
    const modeSaved = mode ? attributes.smn_delivery_mode === mode : !attributes.smn_delivery_mode;
    const markerSaved = marker ? attributes.smn_delivery_test === marker : !attributes.smn_delivery_test;
    return modeSaved && markerSaved;
  };
  const updateCart = async (form, body, signal) => {
    const cartUrl = new URL(form.action, window.location.href);
    if (cartUrl.origin !== window.location.origin) throw new Error('Unexpected cart origin');
    cartUrl.pathname = cartUrl.pathname.replace(/\/$/, '') + '/update.js';
    cartUrl.search = '';
    cartUrl.hash = '';
    const response = await fetch(cartUrl.href, {
      method: 'POST', credentials: 'same-origin',
      headers: {'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      body: JSON.stringify(body), signal
    });
    if (!response.ok) throw new Error('Cart update failed');
    return response.json();
  };
  class DeliveryChoice extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      if (!this.form || this.onSubmit) return;
      this.onSubmit = this.submit.bind(this);
      this.onChoiceClick = this.clickChoice.bind(this);
      this.onClear = this.clearTest.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onChoiceClick, true);
      this.querySelector('[data-smn-clear-test]')?.addEventListener('click', this.onClear);
      this.querySelector('[data-smn-fallback]')?.setAttribute('hidden', '');
      this.querySelectorAll('[data-smn-delivery-mode], [data-smn-clear-test]').forEach((button) => {
        button.disabled = pending || (button.dataset.smnDeliveryMode === 'pickup' && this.dataset.rulesRequired === 'true');
      });
      if (this.dataset.rulesRequired === 'true') void this.refreshRules(true).catch(() => {});
    }
    async rulesProvider() {
      if (window.SMNCartRules) return window.SMNCartRules;
      return new Promise((resolve, reject) => {
        const ready = () => {clearTimeout(timer); window.removeEventListener('smn:cart-rules-ready', ready); resolve(window.SMNCartRules);};
        const timer = setTimeout(() => {window.removeEventListener('smn:cart-rules-ready', ready); reject(new Error(this.dataset.ruleError));}, 5000);
        window.addEventListener('smn:cart-rules-ready', ready, {once: true});
      });
    }
    renderRule(products = [], problem = false) {
      const notice = this.querySelector('[data-smn-rule-notice]');
      const list = this.querySelector('[data-smn-rule-products]');
      const title = this.querySelector('[data-smn-rule-title]');
      const detail = this.querySelector('[data-smn-rule-detail]');
      if (list) {
        list.replaceChildren();
        products.forEach(name => {const item = document.createElement('li'); item.textContent = name; list.appendChild(item);});
      }
      if (title) title.textContent = problem ? this.dataset.ruleError : this.dataset.ruleTitle;
      if (detail) detail.textContent = problem ? '' : this.dataset.ruleDetail;
      if (notice) notice.hidden = !problem && !products.length;
      this.toggleAttribute('data-pickup-restricted', products.length > 0);
      this.querySelector('[data-smn-delivery-mode="pickup"]')?.setAttribute('aria-describedby', `${this.id}-rule`);
    }
    setScope(active) {
      this.scopeActive = active;
      this.dataset.scopeActive = String(active);
      const fallback = this.querySelector('[data-smn-fallback]');
      if (fallback) fallback.hidden = active;
      if (active) {
        const home = this.querySelector('[data-smn-delivery-mode="domicilio"]');
        if (home) home.disabled = pending;
      } else {
        setFields(this);
        this.querySelectorAll('[data-smn-pref-field]').forEach(field => { field.value = ''; });
        this.querySelectorAll('[data-smn-delivery-mode]').forEach(button => { button.disabled = true; });
      }
    }
    async refreshRules(fresh = true, signal) {
      if (this.dataset.rulesRequired !== 'true') return null;
      const revision = (this.rulesRevision || 0) + 1; this.rulesRevision = revision; this.rulesBlocked = true;
      const button = this.querySelector('[data-smn-delivery-mode="pickup"]'); if (button) button.disabled = true;
      try {
        const provider = await this.rulesProvider();
        const metadata = await provider.metadata({fresh, testCart: this.dataset.testMode === 'true', signal});
        const result = metadata.snapshot;
        if (!this.isConnected || this.rulesRevision !== revision) throw new Error('Cart was refreshed');
        const scopeActive = metadata.rules.active === true && (metadata.rules.test_mode === false || this.dataset.testMode === 'true');
        this.setScope(scopeActive);
        this.rulesBlocked = !scopeActive || result.products.length > 0; this.renderRule(scopeActive ? result.products : []);
        if (button) button.disabled = pending || this.rulesBlocked;
        return {...result, scopeActive};
      } catch (error) {
        if (this.isConnected && this.rulesRevision === revision) { this.setScope(false); this.renderRule([], true); }
        throw error;
      }
    }
    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('click', this.onChoiceClick, true);
      this.querySelector('[data-smn-clear-test]')?.removeEventListener('click', this.onClear);
      this.rulesRevision = (this.rulesRevision || 0) + 1;
      this.onSubmit = null;
      this.onChoiceClick = null;
      this.onClear = null;
    }
    clickChoice(event) {
      const button = event.target instanceof Element ? event.target.closest('[data-smn-delivery-mode]') : null;
      if (!button || !this.contains(button)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (button.disabled) return;
      this.startCheckout(button);
    }
    submit(event) {
      const button = event.submitter;
      const mode = button?.dataset.smnDeliveryMode;
      if (!button || !this.contains(button) || !['domicilio', 'pickup'].includes(mode)) return;
      if (event.defaultPrevented) return;
      event.preventDefault();
      this.startCheckout(button);
    }
    async startCheckout(button) {
      const mode = button.dataset.smnDeliveryMode;
      if (!['domicilio', 'pickup'].includes(mode)) return;
      if (pending) return;
      setBusy(true);
      showStatus(this.dataset.loading);
      const marker = this.dataset.testMode === 'true' ? 'true' : '';
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        let snapshot = null;
        if (this.dataset.rulesRequired === 'true') {
          snapshot = await this.refreshRules(true, controller.signal);
          if (!snapshot.scopeActive) throw new Error('Delivery controls are inactive');
          if (mode === 'pickup' && snapshot.products.length) throw new Error(this.dataset.ruleDetail);
        }
        const preference = Object.fromEntries([...this.querySelectorAll('[data-smn-pref-field]')]
          .map(field => [field.dataset.smnPrefField, mode === 'pickup' ? field.value : '']));
        const body = {attributes: {smn_delivery_mode: mode, smn_delivery_test: marker, ...preference}};
        const note = this.form.querySelector('[name="note"]');
        if (note) body.note = note.value;
        const cart = await updateCart(this.form, body, controller.signal);
        if (!saved(cart, mode, marker) || !Number.isInteger(cart.item_count) || cart.item_count < 1) {
          throw new Error('Cart choice was not saved');
        }
        if (snapshot && !window.SMNCartRules.matchesCart(cart, snapshot)) {
          void this.refreshRules(true).catch(() => {});
          throw new Error('Cart products changed');
        }
        if (!this.isConnected || !this.form.isConnected) throw new Error('Cart was refreshed');
        setFields(this, mode, marker);
        let checkout = this.form.querySelector('[data-smn-checkout]');
        if (!checkout) {
          checkout = document.createElement('input');
          checkout.type = 'hidden'; checkout.name = 'checkout'; checkout.value = '1';
          checkout.dataset.smnCheckout = '';
          this.form.appendChild(checkout);
        }
        try { window.SMNDeliveryAnalytics?.checkout(this, mode); } catch {}
        HTMLFormElement.prototype.submit.call(this.form);
      } catch {
        this.form.querySelector('[data-smn-checkout]')?.remove();
        setFields(this);
        showStatus(this.dataset.error, true);
        setBusy(false);
      } finally { clearTimeout(timeout); }
    }
    async clearTest(event) {
      event.preventDefault();
      if (pending) return;
      setBusy(true);
      showStatus(this.dataset.clearing);
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const cart = await updateCart(this.form, {attributes: {smn_delivery_mode: '', smn_delivery_test: '', smn_pickup_flow: '', smn_pickup_pref_id: '', smn_pickup_pref_name: '', smn_pickup_postal: ''}}, controller.signal);
        if (!saved(cart, '', '')) throw new Error('Test attributes were not cleared');
        choices().forEach((choice) => {
          setFields(choice);
          choice.querySelectorAll('[data-smn-pref-field]').forEach(field => { field.value = ''; });
          choice.form?.querySelector('[data-smn-checkout]')?.remove();
        });
        showStatus(this.dataset.cleared);
      } catch { showStatus(this.dataset.clearError, true); }
      finally { clearTimeout(timeout); setBusy(false); }
    }
  }
  customElements.define('smn-delivery-choice', DeliveryChoice);
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    setBusy(false);
    choices().forEach((choice) => {
      setFields(choice);
      const status = choice.querySelector('.smn-delivery-choice__status');
      if (status) status.textContent = '';
      choice.removeAttribute('data-has-error');
      if (choice.dataset.rulesRequired === 'true') void choice.refreshRules(true).catch(() => {});
      choice.form?.querySelector('[data-smn-checkout]')?.remove();
    });
  });

  // SMN fase 3 paso 1: desktop mini-cart to full cart pickup finder.
  const pickupIntent = 'smn_pickup';
  window.addEventListener('click', (event) => {
    const target = event.target instanceof Element
      ? event.target.closest('[data-smn-delivery-mode="pickup"], .smn-pickup-link__button') : null;
    const choice = target?.closest('smn-delivery-choice');
    if (!choice?.classList.contains('smn-delivery-choice--mini-cart')
        || !window.matchMedia('(min-width: 1000px)').matches) return;
    if (target.disabled || pending || choice.getAttribute('aria-busy') === 'true'
        || choice.scopeActive !== true || choice.rulesBlocked !== false) return;
    const form = choice.closest('form');
    if (!form) return;
    const cartUrl = new URL(form.action, window.location.href);
    if (cartUrl.origin !== window.location.origin) return;
    cartUrl.searchParams.set(pickupIntent, '1');
    const previewId = new URL(window.location.href).searchParams.get('preview_theme_id');
    if (previewId) cartUrl.searchParams.set('preview_theme_id', previewId);
    cartUrl.hash = '';
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(cartUrl.href);
  }, true);

  // The URL only requests opening the finder. Availability and checkout remain verified.
  const openRequestedPickup = () => {
    if (new URL(window.location.href).searchParams.get(pickupIntent) !== '1') return;
    let observer;
    let timer;
    let done = false;
    const stop = () => { observer?.disconnect(); clearTimeout(timer); };
    const attempt = () => {
      if (done || pending) return;
      const choice = document.querySelector('smn-delivery-choice.smn-delivery-choice--cart');
      const button = choice?.querySelector('[data-smn-delivery-mode="pickup"]');
      if (!choice?.classList.contains('smn-pickup-enhanced') || choice.scopeActive !== true
          || choice.rulesBlocked !== false || !button || button.disabled) return;
      done = true;
      stop();
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete(pickupIntent);
      try { window.history.replaceState(window.history.state, '', cleanUrl.href); } catch {}
      button.click();
    };
    observer = new MutationObserver(attempt);
    observer.observe(document.documentElement, {childList: true, subtree: true, attributes: true,
      attributeFilter: ['class', 'disabled', 'data-scope-active', 'aria-busy']});
    timer = setTimeout(stop, 10000);
    attempt();
    window.addEventListener('pagehide', stop, {once: true});
  };
  // The finder resets on pageshow, so open only after all initial pageshow handlers.
  if (document.readyState === 'complete') {
    setTimeout(openRequestedPickup, 0);
  } else {
    window.addEventListener('pageshow', () => {
      setTimeout(openRequestedPickup, 0);
    }, {once: true});
  }
})();
